import { apiClient } from '@src/services/http-common'
import {
  ApprovedRemoteExceptions,
  ApprovedRemoteExceptionsParams,
  ApprovedRemoteExceptionsQueryParams,
} from '@src/types/ApprovedRemoteExceptions'
import { ApprovedRemoteExceptionsListDto } from '@src/types/Dto/ApprovedRemoteExceptionsListDto'
import { UseMutateAsyncFunction, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'

const EXCEPTION_SCOPE = 'exceptions'
const LIST = 'list'

const keysFactory = {
  getAllException: (params?: ApprovedRemoteExceptionsQueryParams) => [EXCEPTION_SCOPE, LIST, params || null] as const,
}

export async function getAllException(params?: ApprovedRemoteExceptionsQueryParams) {
  const response = await apiClient.get<ApprovedRemoteExceptions[]>('/ApprovedRemoteExceptions', {
    params: params,
  })
  return response.data
}

export function useGetAllException(
  params?: ApprovedRemoteExceptionsQueryParams,
): [ApprovedRemoteExceptions[], boolean] {
  const { isFetching, data } = useQuery({
    queryFn: () => getAllException(params),
    queryKey: keysFactory.getAllException(params),
  })
  return [data ?? [], isFetching]
}

const postMultipleApprovedRemoteExceptions = async (exceptions: ApprovedRemoteExceptionsListDto) => {
  return await apiClient.post('ApprovedRemoteExceptions', exceptions)
}

export function usePostMultipleApprovedRemoteExceptions(
  params?: ApprovedRemoteExceptionsQueryParams,
): [
  UseMutateAsyncFunction<AxiosResponse, Error, ApprovedRemoteExceptionsListDto, unknown>,
  boolean,
  Error | null,
  boolean,
  boolean,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, error, isSuccess, isError } = useMutation({
    mutationFn: postMultipleApprovedRemoteExceptions,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: keysFactory.getAllException(params),
      })
    },
  })

  return [mutateAsync, isPending, error, isSuccess, isError]
}

export async function deleteApprovedRemoteException(params: ApprovedRemoteExceptionsParams) {
  return await apiClient.delete(`/ApprovedRemoteExceptions/${params.id}/${params.partitionKey}`)
}

export function useDeleteApprovedRemoteException(
  params?: ApprovedRemoteExceptionsQueryParams,
): [
  UseMutateAsyncFunction<AxiosResponse, Error, ApprovedRemoteExceptionsParams, unknown>,
  boolean,
  boolean,
  Error | null,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, isSuccess, error } = useMutation({
    mutationFn: deleteApprovedRemoteException,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: keysFactory.getAllException(params),
      })
    },
  })

  return [mutateAsync, isPending, isSuccess, error]
}
